/**
 * OpeningBalancesWizard — 3-Schritte-Dialog für Übernahmesalden.
 *
 * Schritt 1: Verwalter-Startdatum (aus Property vorbelegt, editierbar)
 * Schritt 2: MV-Liste, pro Zeile: Rückstand Miete, Guthaben, Kaution eingezahlt,
 *            Kautionsart (Bar/Bürgschaft/Verpfändung) inkl. Provider/Doc-Received
 * Schritt 3: Zusammenfassung + „Buchen"
 *
 * Aufruf: PropertyDetail Button „Übernahmesalden erfassen" oder Auto-Trigger
 * beim erstmaligen Anlegen einer Verwaltung (opening_balances_captured_at leer).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { formatEUR } from "@/lib/constants";
import { CalendarBlank, Users, ClipboardText, CheckCircle, Info } from "@phosphor-icons/react";

const STEP_TITLES = ["Verwaltungsbeginn", "Salden pro Mietverhältnis", "Zusammenfassung & Buchen"];

export default function OpeningBalancesWizard({ open, propertyId, onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [managementStart, setManagementStart] = useState("");
  const [tenancies, setTenancies] = useState([]);
  const [entries, setEntries] = useState({});                // { tenancyId: {rent, credit, deposit, deposit_type, ...} }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prop, setProp] = useState(null);

  useEffect(() => {
    if (!open || !propertyId) return;
    setLoading(true);
    setStep(0);
    api.get(`/properties/${propertyId}/opening-balances`)
      .then(({ data }) => {
        setManagementStart((data.management_start || "").slice(0, 10));
        setTenancies(data.tenancies || []);
        setProp({
          management_start: data.management_start,
          opening_balances_captured_at: data.opening_balances_captured_at,
        });
        // Vorbelegung aus prefill
        const pre = {};
        for (const t of data.tenancies || []) {
          pre[t.id] = {
            rent_arrears_eur: t._prefill?.rent_arrears_eur || 0,
            tenant_credit_eur: t._prefill?.tenant_credit_eur || 0,
            deposit_paid_eur: t._prefill?.deposit_paid_eur || 0,
            deposit_type: t.deposit_type || "cash",
            deposit_provider: t.deposit_provider || "",
            deposit_document_received: !!t.deposit_document_received,
            note: "",
          };
        }
        setEntries(pre);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Übernahmesaldo-Daten nicht ladbar"))
      .finally(() => setLoading(false));
  }, [open, propertyId]);

  const updateEntry = (tid, patch) => {
    setEntries((s) => ({ ...s, [tid]: { ...s[tid], ...patch } }));
  };

  const totals = useMemo(() => {
    const list = Object.values(entries);
    return {
      count: tenancies.length,
      with_arrears: list.filter((e) => Number(e.rent_arrears_eur || 0) > 0.01).length,
      with_credit: list.filter((e) => Number(e.tenant_credit_eur || 0) > 0.01).length,
      with_deposit: list.filter((e) => Number(e.deposit_paid_eur || 0) > 0.01).length,
      total_arrears: list.reduce((s, e) => s + (Number(e.rent_arrears_eur) || 0), 0),
      total_credit: list.reduce((s, e) => s + (Number(e.tenant_credit_eur) || 0), 0),
      total_deposit: list.reduce((s, e) => s + (Number(e.deposit_paid_eur) || 0), 0),
    };
  }, [entries, tenancies.length]);

  const submit = async () => {
    if (!managementStart) return toast.error("Startdatum fehlt");
    setBusy(true);
    try {
      const payload = {
        management_start: managementStart,
        entries: tenancies.map((t) => ({
          tenancy_id: t.id,
          rent_arrears_eur: Number(entries[t.id]?.rent_arrears_eur || 0),
          tenant_credit_eur: Number(entries[t.id]?.tenant_credit_eur || 0),
          deposit_paid_eur: Number(entries[t.id]?.deposit_paid_eur || 0),
          deposit_type: entries[t.id]?.deposit_type || "cash",
          deposit_provider: entries[t.id]?.deposit_provider || "",
          deposit_document_received: !!entries[t.id]?.deposit_document_received,
          note: entries[t.id]?.note || "",
        })),
      };
      const { data } = await api.post(`/properties/${propertyId}/opening-balances`, payload);
      toast.success(`Übernahme gebucht: ${data.postings_created} Posten für ${data.tenancies_touched} MV.`);
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Buchen");
    } finally {
      setBusy(false);
    }
  };

  const stepIcon = (i) => [CalendarBlank, Users, CheckCircle][i];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl" data-testid="opening-balances-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardText size={18} /> Übernahmesalden erfassen
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          {STEP_TITLES.map((title, i) => {
            const Icon = stepIcon(i);
            const active = step === i;
            const done = step > i;
            return (
              <div key={i} className="flex items-center gap-2">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                  done ? "bg-emerald-600 text-white"
                  : active ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"
                }`}>
                  {done ? <CheckCircle size={14} weight="fill" /> : <Icon size={14} />}
                </div>
                <span className={`text-xs ${active ? "font-semibold text-slate-900" : "text-slate-500"}`}>
                  {title}
                </span>
                {i < STEP_TITLES.length - 1 && <span className="w-6 border-t border-slate-200"></span>}
              </div>
            );
          })}
        </div>

        {loading && <div className="py-8 text-center text-slate-500 text-sm">Lade Objekt und Mietverhältnisse …</div>}

        {!loading && step === 0 && (
          <div className="py-4 space-y-3">
            <p className="text-sm text-slate-600">
              Ab welchem Datum übernehmen Sie die Verwaltung dieses Objekts? Die Übernahmesalden werden
              exakt auf dieses Datum gebucht.
            </p>
            <div className="max-w-xs">
              <Label className="text-xs uppercase font-semibold text-slate-500">Verwaltungsbeginn</Label>
              <Input type="date" value={managementStart}
                     onChange={(e) => setManagementStart(e.target.value)}
                     data-testid="wizard-management-start" />
            </div>
            {prop?.opening_balances_captured_at && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                <Info size={13} className="mt-0.5 flex-shrink-0" />
                <span>
                  Für dieses Objekt wurden bereits Übernahmesalden erfasst
                  ({new Date(prop.opening_balances_captured_at).toLocaleDateString("de-DE")}).
                  Beim erneuten Speichern werden alle bisherigen Übernahme-Buchungen ersetzt.
                </span>
              </div>
            )}
          </div>
        )}

        {!loading && step === 1 && (
          <div className="py-3 max-h-[65vh] overflow-y-auto">
            {tenancies.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8">
                Keine aktiven Mietverhältnisse im Objekt.
              </div>
            ) : (
              <div className="space-y-3">
                {tenancies.map((t) => {
                  const e = entries[t.id] || {};
                  const label = t.tenant_display || t.contract_number || t.id.slice(0, 8);
                  return (
                    <div key={t.id} className="border border-slate-200 rounded-md p-3 bg-slate-50/50" data-testid={`wizard-entry-${t.id}`}>
                      <div className="flex items-baseline justify-between mb-2">
                        <div>
                          <span className="font-semibold text-slate-900 text-sm">{label}</span>
                          <span className="ml-2 font-mono text-xs text-slate-500">{t.contract_number}</span>
                        </div>
                        <span className="text-[10px] text-slate-500">
                          Kaution laut Vertrag: {formatEUR(t.deposit || 0)}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Rückstand Miete</Label>
                          <Input type="number" step="0.01" value={e.rent_arrears_eur || ""}
                                 onChange={(ev) => updateEntry(t.id, { rent_arrears_eur: ev.target.value })}
                                 placeholder="0.00" data-testid={`wizard-arrears-${t.id}`} className="h-8" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Guthaben Mieter</Label>
                          <Input type="number" step="0.01" value={e.tenant_credit_eur || ""}
                                 onChange={(ev) => updateEntry(t.id, { tenant_credit_eur: ev.target.value })}
                                 placeholder="0.00" data-testid={`wizard-credit-${t.id}`} className="h-8" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Kaution eingezahlt</Label>
                          <Input type="number" step="0.01" value={e.deposit_paid_eur || ""}
                                 onChange={(ev) => updateEntry(t.id, { deposit_paid_eur: ev.target.value })}
                                 placeholder="0.00" data-testid={`wizard-deposit-${t.id}`} className="h-8" />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Kautionsart</Label>
                          <Select value={e.deposit_type || "cash"}
                                  onValueChange={(v) => updateEntry(t.id, { deposit_type: v })}>
                            <SelectTrigger className="h-8" data-testid={`wizard-deposit-type-${t.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash">Barkaution</SelectItem>
                              <SelectItem value="buergschaft">Bürgschaft</SelectItem>
                              <SelectItem value="verpfaendung">Verpfändung</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {(e.deposit_type === "buergschaft" || e.deposit_type === "verpfaendung") && (
                          <>
                            <div>
                              <Label className="text-[10px] uppercase text-slate-500">Bank/Versicherung</Label>
                              <Input value={e.deposit_provider || ""}
                                     onChange={(ev) => updateEntry(t.id, { deposit_provider: ev.target.value })}
                                     placeholder="Provider" className="h-8" />
                            </div>
                            <div className="flex items-end gap-2 pb-1">
                              <Switch checked={!!e.deposit_document_received}
                                      onCheckedChange={(v) => updateEntry(t.id, { deposit_document_received: v })} />
                              <span className="text-xs text-slate-600">Urkunde vorgelegt</span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="mt-2">
                        <Label className="text-[10px] uppercase text-slate-500">Notiz (optional)</Label>
                        <Textarea rows={1} value={e.note || ""}
                                  onChange={(ev) => updateEntry(t.id, { note: ev.target.value })}
                                  placeholder="z. B. Übernahme laut Kaufvertrag §5"
                                  className="text-xs" data-testid={`wizard-note-${t.id}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!loading && step === 2 && (
          <div className="py-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <SummaryTile label="Rückstände" value={totals.total_arrears} count={totals.with_arrears} color="rose" />
              <SummaryTile label="Guthaben" value={totals.total_credit} count={totals.with_credit} color="emerald" />
              <SummaryTile label="Kaution eingezahlt" value={totals.total_deposit} count={totals.with_deposit} color="sky" />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600">
              <p>
                <strong>Verwaltungsbeginn:</strong>{" "}
                {managementStart ? new Date(managementStart).toLocaleDateString("de-DE") : "-"}
              </p>
              <p><strong>Betroffene Mietverhältnisse:</strong> {totals.count}</p>
              <p className="mt-2">
                Beim Klick auf &bdquo;Buchen&ldquo; werden alle Übernahme-Buchungen zum genannten Datum angelegt.
                Bestehende Übernahme-Buchungen für dieses Objekt werden durch die neuen ersetzt.
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between">
          <div className="text-[11px] text-slate-500">Schritt {step + 1} von {STEP_TITLES.length}</div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)} disabled={busy}>Zurück</Button>
            )}
            {step < 2 && (
              <Button onClick={() => setStep((s) => s + 1)}
                      disabled={busy || (step === 0 && !managementStart)}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid="wizard-next-btn">Weiter</Button>
            )}
            {step === 2 && (
              <Button onClick={submit} disabled={busy}
                      className="bg-emerald-700 hover:bg-emerald-600"
                      data-testid="wizard-submit-btn">
                {busy ? "Bucht …" : "Übernahmesalden buchen"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryTile({ label, value, count, color }) {
  const map = {
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    sky: "bg-sky-50 border-sky-200 text-sky-800",
  };
  return (
    <div className={`border rounded p-3 text-center ${map[color]}`}>
      <div className="text-[10px] uppercase font-semibold tracking-wider">{label}</div>
      <div className="text-lg font-bold tabular-nums mt-1">{formatEUR(value)}</div>
      <div className="text-[10px] mt-0.5">{count} MV</div>
    </div>
  );
}
